import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { MessageSquare, Users, CreditCard, FileText, Plane, Check } from "lucide-react";
import SEO, { pageSEO } from "../components/SEO";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Inquire",
    description: "Fill out our short inquiry form or schedule a 15-minute call. Tell us about yourself, your travel preferences, and any questions you have about the journey.",
    details: [
      "No commitment required",
      "Response within 24 hours",
      "Option for phone or video call"
    ]
  },
  {
    number: "02",
    icon: Users,
    title: "Confirm Fit",
    description: "We'll have a quick conversation to ensure the dates, pacing, and experience match your expectations. This is your chance to ask detailed questions and learn more about your fellow travelers.",
    details: [
      "Discuss your fitness level and mobility",
      "Review dietary preferences",
      "Ensure travel dates work"
    ]
  },
  {
    number: "03",
    icon: CreditCard,
    title: "Reserve Your Spot",
    description: "Secure your place with a deposit. Given our small group size (max 6-8 guests), spots are limited. Your remaining balance is due 90 days before departure.",
    details: [
      "$1,500 deposit to reserve",
      "Balance due 90 days prior (refundable till 45 days prior)",
      "Secure payment processing"
    ]
  },
  {
    number: "04",
    icon: FileText,
    title: "Prepare",
    description: "Receive your comprehensive pre-departure pack with everything you need to get ready for India — from visa guidance to packing lists to cultural tips.",
    details: [
      "Visa application guidance",
      "Flight recommendations from NYC/NJ",
      "Packing list & health tips",
      "Travel insurance recommendations"
    ]
  },
  {
    number: "05",
    icon: Plane,
    title: "Travel",
    description: "Arrive in Delhi and leave everything to us. Aaron and our team handle every detail from the moment you land until your departure — you simply need to be curious and present.",
    details: [
      "Airport pickup arranged",
      "24/7 host support",
      "All logistics handled"
    ]
  }
];

export default function HowItWorksPage() {
  return (
    <div className="pt-24 md:pt-32" data-testid="how-it-works-page">
      <SEO {...pageSEO.howItWorks} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Simple Process</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            How It <span className="text-[#D4AF37]">Works</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            From your first inquiry to your departure, we make every step simple and transparent.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto space-y-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="relative bg-white rounded-2xl p-8 md:p-10 shadow-soft"
                data-testid={`step-${index}`}
              >
                {/* Connection line */}
                {index < steps.length - 1 && (
                  <div className="absolute left-12 md:left-14 top-full h-8 w-0.5 bg-[#D4AF37]/30" />
                )}
                
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  {/* Number & Icon */}
                  <div className="flex items-center gap-4 md:flex-col md:items-start md:w-24">
                    <div className="w-16 h-16 rounded-full bg-[#243b5a] flex items-center justify-center flex-shrink-0">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <span className="text-[#D4AF37] text-4xl font-serif font-semibold">{step.number}</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl text-[#243b5a] mb-4">{step.title}</h3>
                    <p className="text-[#4A4A4A] leading-relaxed mb-6">{step.description}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                          <span className="text-[#4A4A4A] text-sm">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust indicators */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
            <div className="p-6">
              <div className="font-serif text-5xl text-[#243b5a] mb-2">24h</div>
              <p className="text-[#4A4A4A]">Response time on all inquiries</p>
            </div>
            <div className="p-6">
              <div className="font-serif text-5xl text-[#243b5a] mb-2">6-8</div>
              <p className="text-[#4A4A4A]">Maximum guests per departure</p>
            </div>
            <div className="p-6">
              <div className="font-serif text-5xl text-[#243b5a] mb-2">100%</div>
              <p className="text-[#4A4A4A]">Refundable until 45 days prior</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#243b5a]">
        <div className="container-max text-center text-white">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Begin <span className="text-[#D4AF37]">Step One</span>?
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Fill out our quick inquiry form and we'll be in touch within 24 hours.
          </p>
          <Link to="/contact">
            <Button className="btn-gold" data-testid="start-inquiry-btn">
              Start Your Inquiry
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
